import { useState } from 'react';
import { voteAPI } from '../lib/api';
import { toast } from 'sonner';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

export const VoteButtons = ({ 
  targetType, 
  targetId, 
  initialUpvotes = 0, 
  initialDownvotes = 0, 
  userVote = null,
  size = 'default',
  onVoteChange 
}) => {
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [downvotes, setDownvotes] = useState(initialDownvotes);
  const [currentVote, setCurrentVote] = useState(userVote);
  const [isVoting, setIsVoting] = useState(false);

  const score = upvotes - downvotes;

  const handleVote = async (vote) => {
    if (isVoting) return;
    
    const newVote = currentVote === vote ? 0 : vote;
    setIsVoting(true);

    try {
      const response = await voteAPI.vote(targetType, targetId, newVote);
      setUpvotes(response.data.upvotes);
      setDownvotes(response.data.downvotes);
      setCurrentVote(response.data.user_vote);
      
      if (onVoteChange) {
        onVoteChange(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please sign in to vote');
      } else {
        toast.error('Failed to vote');
      }
    } finally {
      setIsVoting(false);
    }
  };

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const buttonSize = size === 'sm' ? 'p-1' : 'p-1.5';

  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={`vote-buttons-${targetId}`}>
      <button
        onClick={() => handleVote(1)}
        disabled={isVoting}
        className={cn(
          'vote-btn rounded-md hover:bg-emerald-50 disabled:opacity-50',
          buttonSize,
          currentVote === 1 ? 'text-emerald-600' : 'text-slate-400 hover:text-emerald-600'
        )}
        data-testid={`upvote-btn-${targetId}`}
        aria-label="Upvote"
      >
        <ChevronUp className={cn(iconSize, currentVote === 1 && 'fill-current')} />
      </button>
      
      <span
        className={cn(
          'vote-count text-sm font-semibold',
          score > 0 ? 'text-emerald-600' : score < 0 ? 'text-red-500' : 'text-slate-500'
        )}
        data-testid={`vote-score-${targetId}`}
      >
        {score}
      </span>
      
      <button
        onClick={() => handleVote(-1)}
        disabled={isVoting}
        className={cn(
          'vote-btn rounded-md hover:bg-red-50 disabled:opacity-50',
          buttonSize,
          currentVote === -1 ? 'text-red-500' : 'text-slate-400 hover:text-red-500'
        )}
        data-testid={`downvote-btn-${targetId}`}
        aria-label="Downvote"
      >
        <ChevronDown className={cn(iconSize, currentVote === -1 && 'fill-current')} />
      </button>
    </div>
  );
};

export default VoteButtons;
